/**
 * Cart Drawer JavaScript
 * Handles cart drawer functionality and state management
 */

(function() {
  'use strict';

  // Initialize cart drawer
  function initCartDrawer() {
    const cartDrawer = document.getElementById('sidebar-cart');
    if (!cartDrawer) {
      return;
    }

    function fetchAndUpdateCount() {
      const rootUrl = window.Shopify?.routes?.root || '/';
      fetch(rootUrl + 'cart.js', {
        credentials: 'same-origin'
      })
      .then(res => res.json())
      .then(cart => {
        const itemCount = cart.item_count || 0;
        cartDrawer.setAttribute('data-item-count', itemCount);
        updateCartDrawerCount(itemCount);
        updateCheckoutButton();
      });
    }

    
    // Update checkout button state based on cart items
    function updateCheckoutButton() {
      const checkoutButton = cartDrawer.querySelector('.CartDrawer__CheckoutButton');
      if (!checkoutButton) {
        return;
      }

      // Check if cart has items (this will be updated via AJAX cart updates)
      const cartItemCount = parseInt(cartDrawer.getAttribute('data-item-count') || '0'); 
      if (cartItemCount === 0) {
        checkoutButton.classList.add('CartDrawer__CheckoutButton--disabled');
        checkoutButton.disabled = true;
      } else {
        checkoutButton.classList.remove('CartDrawer__CheckoutButton--disabled');
        checkoutButton.disabled = false;
      }
    }

    // Update cart drawer count display
    function updateCartDrawerCount(itemCount) {
      const cartDrawerCount = cartDrawer.querySelector('.CartDrawer__Count');
      if (cartDrawerCount) {
        cartDrawerCount.textContent = itemCount;
      }
      
      // Also update header cart count if it exists
      const headerCartCount = document.querySelector('.Header__CartCount');
      if (headerCartCount) {
        headerCartCount.textContent = itemCount;
        if (itemCount === 0) {
          headerCartCount.classList.remove('is-visible');
        } else {
          headerCartCount.classList.add('is-visible');
        }
      }
      
      // Update header cart dot if it exists
      const headerCartDot = document.querySelector('.Header__CartDot');
      if (headerCartDot) {
        if (itemCount === 0) {
          headerCartDot.classList.remove('is-visible');
        } else {
          headerCartDot.classList.add('is-visible');
        }
      }
    }

    // Clear all cart items
    function clearCart() {
      const removeAllButton = cartDrawer.querySelector('[data-action="clear-cart"]');
      if (!removeAllButton) {
        return;
      }

      // Disable button during clearing
      removeAllButton.disabled = true;

      // IMMEDIATELY update count to 0 for instant feedback
      cartDrawer.setAttribute('data-item-count', '0');
      updateCartDrawerCount(0);
      updateCheckoutButton();

      // Get all cart items with data-line-id
      const cartItems = cartDrawer.querySelectorAll('[data-line-id]');
      const updates = {};

      // Build updates object with all items set to quantity 0
      cartItems.forEach(function(item) {
        const lineId = item.getAttribute('data-line-id');
        if (lineId) {
          updates[lineId] = 0;
        }
      });

      // Use cart/update.js to clear all items at once
      const rootUrl = window.Shopify?.routes?.root || '/';
      fetch(rootUrl + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ updates: updates })
      }).then(function(response) {
        return response.json();
      }).then(function(cart) {
        // Ensure count is 0
        const itemCount = cart.item_count || 0;
        cartDrawer.setAttribute('data-item-count', itemCount);
        updateCartDrawerCount(itemCount);
        updateCheckoutButton();
        
        // Trigger cart refresh event
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        
        // Dispatch cart updated event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { item_count: itemCount }
        }));

        // Reload to show empty cart state
        setTimeout(function() {
          window.location.reload();
        }, 300);
      }).catch(function(error) {
        console.error('Error clearing cart:', error);
        removeAllButton.disabled = false;
      });
    }

    // Handle clear cart button click
    cartDrawer.addEventListener('click', function(event) {
      if (event.target.closest('[data-action="clear-cart"]')) {
        event.preventDefault();
        clearCart();
      }
    });

    // Listen for cart updates to update count
    document.addEventListener('cart:updated', function(event) {
      if (event.detail && event.detail.item_count !== undefined) {
        const itemCount = event.detail.item_count;
        cartDrawer.setAttribute('data-item-count', itemCount);
        updateCheckoutButton();
        updateCartDrawerCount(itemCount);
      }
    });

    // Listen for product:added event to keep count in sync with theme cart section
    document.addEventListener('product:added', function(event) {
      // The core theme cart section already updates the header/cart counts
      // on product:added. To avoid double-increment flicker, we only
      // re-sync from the actual cart here.
      fetchAndUpdateCount();
    });

    // Listen for cart:refresh to update count (when theme.js refreshes cart)
    document.addEventListener('cart:refresh', function() {
      // Fetch cart to get updated count
      const rootUrl = window.Shopify?.routes?.root || '/';
      fetch(rootUrl + 'cart.js', {
        credentials: 'same-origin',
        method: 'GET'
      }).then(function(response) {
        return response.json();
      }).then(function(cart) {
        const itemCount = cart.item_count || 0;
        cartDrawer.setAttribute('data-item-count', itemCount);
        updateCheckoutButton();
        updateCartDrawerCount(itemCount);
      }).catch(function(error) {
        console.error('Error fetching cart:', error);
      });
    });

    // Prevent Enter key on quantity inputs from submitting the cart form
    cartDrawer.addEventListener('keydown', function(event) {
      const target = event.target;
      if (!target) return;

      const isQuantityInput = target.classList && target.classList.contains('QuantitySelector__CurrentQuantity');
      if (!isQuantityInput) return;

      const key = event.key || event.keyCode;
      const isEnter = key === 'Enter' || key === 13;

      if (isEnter) {
        event.preventDefault();
        event.stopPropagation();

        if (typeof target.blur === 'function') {
          target.blur();
        }
      }
    });

    // Intercept cart/change.js and cart/add.js calls to keep counts in sync
    // This ensures count updates instantly for ALL quantity changes (buttons + input field)
    // and also when products are added through AJAX (including app scripts).
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      const options = args[1] || {};
      
      const isChangeRequest = typeof url === 'string' && url.includes('/cart/change.js') && options.method === 'POST';
      const isAddRequest = typeof url === 'string' && url.includes('/cart/add.js') && options.method === 'POST';
      
      // Only intercept specific cart endpoints; everything else goes through untouched
      if (isChangeRequest || isAddRequest) {
        return originalFetch.apply(this, args).then(function(response) {
          const clonedResponse = response.clone();

          clonedResponse.json().then(function(payload) {
            const currentCount = parseInt(cartDrawer.getAttribute('data-item-count') || '0');

            // For quantity changes, Shopify usually returns a full cart object.
            // For add.js, apps may get back either the added line item or an error object.
            const hasValidItemCount = payload && typeof payload.item_count === 'number';
            const isErrorStatus = !response.ok || (payload && typeof payload.status === 'number' && payload.status >= 400);

            if (!hasValidItemCount || isErrorStatus) {
              // Something went wrong (for example, requested quantity > inventory and
              // Shopify responds 422 with "Only X items were added..." message).
              // In all these cases, we re-sync from the real cart.
              fetchAndUpdateCount();

              // If this was an add.js call, also trigger product:added so CartSection
              // re-renders the drawer & opens it — but with quantity 0 so we do NOT
              // temporarily inflate the cart count (no visual flicker).
              if (isAddRequest) {
                document.dispatchEvent(new CustomEvent('product:added', {
                  detail: {
                    variant: null,
                    quantity: 0
                  }
                }));
              }

              return;
            }

            // Successful update with a valid cart payload: update count immediately
            const itemCount = payload.item_count;
            cartDrawer.setAttribute('data-item-count', itemCount);
            updateCartDrawerCount(itemCount);
            updateCheckoutButton();

            document.dispatchEvent(new CustomEvent('cart:updated', {
              detail: { item_count: itemCount }
            }));
          }).catch(function(error) {
            // Silently fail - theme.js or other code will handle the error
          });

          return response;
        });
      }
      
      // For all other fetch calls, use original fetch
      return originalFetch.apply(this, args);
    };

    // Watch for changes to data-item-count attribute and update count display
    const countObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-item-count') {
          const itemCount = parseInt(cartDrawer.getAttribute('data-item-count') || '0');
          updateCartDrawerCount(itemCount);
          updateCheckoutButton();
        }
      });
    });

    countObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['data-item-count']
    });

    // Initial update - fetch actual count on page load
    fetchAndUpdateCount();

    // Update on drawer open
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          if (cartDrawer.getAttribute('aria-hidden') === 'false') { 
            
            updateCheckoutButton();
            // Also update count from attribute when drawer opens
            const itemCount = parseInt(cartDrawer.getAttribute('data-item-count') || '0');
            updateCartDrawerCount(itemCount);
            fetchAndUpdateCount();
          }
        }
      });
    });

    observer.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['aria-hidden']
    });

    // Auto-open drawer when redirected with ?cart=true
    (function openDrawerFromQueryParam() {
      const searchParams = new URLSearchParams(window.location.search);
      if ((searchParams.get('open-cart') || '').toLowerCase() !== 'true') {
        return;
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('open-cart');
      window.history.replaceState({}, '', url);
      document.dispatchEvent(new CustomEvent('product:added', {
        detail: {
          variant: null,
          quantity: 0
        }
      }));
    })();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawer);
  } else {
    initCartDrawer();
  }

  // Re-initialize after AJAX cart updates (Shopify theme compatibility)
  if (typeof document.addEventListener !== 'undefined') {
    document.addEventListener('shopify:section:load', initCartDrawer);
  }
})();
